import { Box, Grid } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import InputDisabled from '../../../atoms/inputDisabled';
import Typography from '../../../atoms/typography';
import Input from '../../../atoms/input';
import { useForm } from 'react-hook-form';
import ButtonComponent from '../../../atoms/button';
import { ReactComponent as SearchIcon } from '../../../../assets/icons/icon-search.svg';
import IconButton from '@mui/material/IconButton';
import { DistributorForm } from '../../../../services/types';
import { ESTADOS } from '../../../../constants/constants';
import useViaCep from '../../../../utils/viaCep';
import SelectCustom from '../../../atoms/selectCustom';
import { SelectChangeEvent } from '@mui/material/Select';
import ModalCustom from '../../../atoms/modalCustom';
import useUpdateDistributor from '../../../../hooks/distributors/useUpdateDistributor';
import useGetDistributorData from '../../../../hooks/distributors/useGetDistributorData';
import { useSelector } from 'react-redux';
import jwtDecode from 'jwt-decode';
import { ApplicationState } from '../../../../store';
import { yupResolver } from '@hookform/resolvers/yup';
import schema, { formSchemaType } from './schema';
import usePatchDistributor from '../../../../hooks/distributors/usePatchDistributor';
import useRegisterDistributor from '../../../../hooks/distributors/useRegisterDistributor';
import ActivityIndicator from '../../../ActivityIndicator';

const CompanyData: React.FC = () => {
  const { execute: executeViaCep } = useViaCep();
  const [modalPublishOpen, setModalPublishOpen] = useState(false);
  const [isDataChanged, setIsDataChanged] = useState(false);
  const { execute: UseGetDistributor } = useGetDistributorData();
  const { execute: updateDistributor, loading: loadingUpdate } =
    useUpdateDistributor();

  const { control, setValue } = useForm<formSchemaType>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      zipCode: '',
      state: '',
      city: '',
      neighborhood: '',
      streetAddress: '',
      streetNumber: '',
      phone: '',
      complement: '',
      cellPhone: '',
      email: '',
    },
  });

  const token = useSelector(
    (state: ApplicationState) => state.distributor.token,
  );
  const tokenDecoded: any = jwtDecode(token);
  const [distributor, setDistributor] = useState<DistributorForm>({
    name: '',
    cnpj: '',
    zipCode: '',
    state: '',
    city: '',
    neighborhood: '',
    streetAddress: '',
    streetNumber: '',
    phone: '',
    complement: '',
    cellPhone: '',
    email: '',
    fee: '0',
    feeType: 'Percent',
    stateRegistration: ESTADOS[0].sigla,
  });

  const handleViaCep = async () => {
    await executeViaCep(distributor.zipCode, (response: any) => {
      setDistributor({
        ...distributor,
        state: response.uf,
        city: response.localidade,
        neighborhood: response.bairro,
        zipCode: response.cep,
        streetAddress: response.logradouro,
      });
    });
  };

  const handleOpenModal = () => {
    setModalPublishOpen(true);
  };

  const handleChange = async (value: string, field: keyof DistributorForm) => {
    setDistributor({
      ...distributor,
      [field]: value,
    });
    setIsDataChanged(true);
  };

  const handleUpdateDistributor = async () => {
    await updateDistributor(distributor, tokenDecoded.distributorId);
    setModalPublishOpen(false);
    setIsDataChanged(false);
  };

  const getDistributorData = async () => {
    if (tokenDecoded.distributorId?.length) {
      const distributorData = await UseGetDistributor(
        tokenDecoded.distributorId,
      );
      setDistributor({
        ...distributorData,
      });
    }
  };

  useEffect(() => {
    getDistributorData();
    return () => {
      setDistributor({
        name: '',
        cnpj: '',
        zipCode: '',
        state: '',
        city: '',
        neighborhood: '',
        streetAddress: '',
        streetNumber: '',
        phone: '',
        fee: '0',
        feeType: 'Percent',
        stateRegistration: ESTADOS[0].sigla,
        complement: '',
        cellPhone: '',
        email: '',
      });
    };
  }, []);

  const [originalDistributor, setOriginalDistributor] =
    useState<DistributorForm>({
      name: '',
      cnpj: '',
      zipCode: '',
      state: '',
      city: '',
      neighborhood: '',
      streetAddress: '',
      streetNumber: '',
      phone: '',
      complement: '',
      cellPhone: '',
      email: '',
      fee: '0',
      feeType: 'Percent',
      stateRegistration: ESTADOS[0].sigla,
    });

  useEffect(() => {
    setOriginalDistributor({ ...distributor });
  }, [distributor]);

  const handleCancelChanges = () => {
    setDistributor({ ...originalDistributor });
    setIsDataChanged(false);

    window.location.reload();
  };

  if (loadingUpdate) {
    return <ActivityIndicator />;
  }
  return (
    <>
      <ModalCustom
        open={modalPublishOpen}
        onCancelClick={() => setModalPublishOpen(false)}
        label={'Alteração nos dados da empresa'}
        children={'Tem certeza que deseja alterar os dados da empresa?'}
        onClickPublish={handleUpdateDistributor}
        ButtonOutlinePrimary="Cancelar"
        ButtonPrimary="Alterar"
      />
      <Box component="div">
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <InputDisabled
              size="small"
              label={'CNPJ'}
              mask="99.999.999/9999-99"
              value={distributor.cnpj}
            />
          </Grid>

          <Grid item xs={6}>
            <InputDisabled
              size="small"
              label={'Razão Social'}
              value={distributor.name}
            />
          </Grid>
        </Grid>
        <hr />
        <Typography color="secondary" fontWeight="bold" lineHeight={24}>
          Informações de Contato
        </Typography>
        <Grid container spacing={2} style={{ marginTop: 4 }}>
          <Grid item xs={4}>
            <Input
              size="small"
              label={'Telefone'}
              name="phone"
              placeholder="5584991113834"
              mask="(99)99999-9999"
              control={control}
              maxLength={20}
              value={distributor.phone}
              onChange={event => {
                const rawValue = event.target.value.replace(/[^\d]/g, '');
                handleChange(rawValue, 'phone');
                setValue('phone', rawValue, { shouldValidate: true });
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              size="small"
              label={'WhatsApp'}
              name="cellPhone"
              mask="+99(99)99999-9999"
              control={control}
              value={distributor.cellPhone}
              onChange={event => {
                const rawValue = event.target.value.replace(/[^\d]/g, '');
                handleChange(rawValue, 'cellPhone');

                setValue('cellPhone', rawValue, { shouldValidate: true });
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              size="small"
              label={'E-mail'}
              name="email"
              defaultValue="multigiro@fretapp.com"
              control={control}
              placeholder="distributor@fretapp.com"
              value={distributor.email}
              onChange={event => {
                const rawValue = event.target.value.trim();
                handleChange(rawValue, 'email');
                setValue('email', rawValue, { shouldValidate: true });
              }}
            />
          </Grid>
        </Grid>
        <hr />
        <Typography color="secondary" fontWeight="bold" lineHeight={24}>
          Endereço
        </Typography>
        <Grid container spacing={2} style={{ marginTop: 4 }}>
          <Grid item xs={3}>
            <Input
              size="small"
              label={'CEP'}
              name="zipCode"
              mask="99999-999"
              control={control}
              value={distributor.zipCode}
              onChange={event => {
                const rawValue = event.target.value.replace(/[^\d]/g, '');
                handleChange(rawValue, 'zipCode');
                setValue('zipCode', rawValue, { shouldValidate: true });
              }}
              endAdornment={
                <IconButton onClick={handleViaCep}>
                  <SearchIcon />
                </IconButton>
              }
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              size="small"
              label={'Endereço'}
              name="address"
              control={control}
              maxLength={50}
              value={distributor.streetAddress}
              onChange={event => {
                const rawValue = event.target.value.replace(/[^\w\s]/g, '');
                handleChange(rawValue, 'streetAddress');
                setValue('streetAddress', rawValue, { shouldValidate: true });
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <Input
              size="small"
              label={'Número'}
              placeholder="585"
              name="number"
              control={control}
              maxLength={5}
              value={distributor.streetNumber}
              onChange={event => {
                const rawValue = event.target.value.replace(/[^\d]/g, '');
                handleChange(rawValue, 'streetNumber');
                setValue('streetNumber', rawValue, { shouldValidate: true });
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <Input
              size="small"
              label={'Complemento'}
              placeholder="Lj C1"
              name="complement"
              control={control}
              maxLength={30}
              value={distributor.complement}
              onChange={event => {
                const rawValue = event.target.value.replace(/[^\w\s]/g, '');
                handleChange(rawValue, 'complement');
                setValue('complement', rawValue, { shouldValidate: true });
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} style={{ marginTop: 4 }}>
          <Grid item xs={4}>
            <Input
              size="small"
              label={'Bairro'}
              name="neighborhood"
              control={control}
              maxLength={20}
              value={distributor.neighborhood}
              onChange={event => {
                const rawValue = event.target.value.replace(/[^\w\s]/g, '');
                handleChange(rawValue, 'neighborhood');
                setValue('neighborhood', rawValue, { shouldValidate: true });
              }}
              onBlur={handleViaCep}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              size="small"
              label={'Cidade'}
              name="city"
              control={control}
              maxLength={20}
              value={distributor.city}
              onChange={event => {
                const rawValue = event.target.value.replace(/[^\w\s]/g, '');
                handleChange(rawValue, 'city');
                setValue('city', rawValue, { shouldValidate: true });
              }}
              onBlur={handleViaCep}
            />
          </Grid>
          <Grid item xs={4}>
            <SelectCustom
              size="small"
              label={'Estado'}
              value={distributor.state}
              defaultValue={'Paraíba'}
              onChange={(event: SelectChangeEvent<unknown>) =>
                setDistributor({
                  ...distributor,
                  stateRegistration: event.target.value as string,
                })
              }
              options={ESTADOS.map(option => ({
                value: option.sigla,
                label: option.unidadeFederativa,
              }))}
            />
          </Grid>
        </Grid>
        <Grid
          container
          justifyContent="space-between"
          style={{ marginTop: 20 }}
        >
          <ButtonComponent
            theme="outline-primary"
            justifyContent="flex-start"
            flex={1}
            onClick={handleCancelChanges}
          >
            Cancelar
          </ButtonComponent>
          <ButtonComponent
            theme="primary"
            justifyContent="flex-end"
            flex={1}
            onClick={handleOpenModal}
            disabled={!isDataChanged}
          >
            Salvar alterações
          </ButtonComponent>
        </Grid>
      </Box>
    </>
  );
};
export default CompanyData;
